/**
 * MapLocationPicker - Interactive modal for selecting geographic locations
 *
 * Combines search, click-to-place, and drag-to-fine-tune for an intuitive
 * location selection experience.
 *
 * Workflow:
 * 1. User opens modal for a stage
 * 2. Searches for location OR clicks on map
 * 3. Marker appears (draggable)
 * 4. Reverse geocoding shows place name
 * 5. User confirms → position saved
 */

import {
  GeocodingService,
  type SearchSuggestion,
} from '../src/services/GeocodingService';
import {
  getTileLayerConfig,
  DEFAULT_MAP_CENTER,
  type MapStyle,
} from '../src/config/mapTileLayers';
import { showAlert } from '../src/ui/ModalManager';

declare const L: typeof import('leaflet');

export interface MapLocationPickerOptions {
  /** Initial position (if editing existing marker) */
  initialPosition?: { lat: number; lng: number };
  /** Initial zoom level */
  initialZoom?: number;
  /** Map style */
  mapStyle?: MapStyle;
  /** Stage name to show in header */
  stageName?: string;
  /** Callback when user confirms location */
  onConfirm: (position: { lat: number; lng: number }, placeName?: string) => void;
  /** Callback when user cancels */
  onCancel: () => void;
}

export class MapLocationPicker {
  private options: MapLocationPickerOptions;
  private geocodingService: GeocodingService;
  private modalElement: HTMLElement | null = null;
  private map: L.Map | null = null;
  private marker: L.Marker | null = null;
  private currentPlaceName: string | null = null;
  private searchDebounceTimer: number | null = null;

  // DOM references
  private searchInput: HTMLInputElement | null = null;
  private suggestionsContainer: HTMLElement | null = null;
  private placeNameDisplay: HTMLElement | null = null;
  private latInput: HTMLInputElement | null = null;
  private lngInput: HTMLInputElement | null = null;
  private confirmBtn: HTMLButtonElement | null = null;

  constructor(options: MapLocationPickerOptions) {
    this.options = options;
    this.geocodingService = GeocodingService.getInstance();
  }

  /**
   * Open the location picker modal with error handling
   */
  async open(): Promise<void> {
    try {
      this.injectStyles();
      await this.loadLeaflet();
      this.createModal();
      this.initializeMap();
      this.setupEventListeners();

      // Animate in
      requestAnimationFrame(() => {
        this.modalElement?.classList.add('visible');
      });
    } catch (error) {
      console.error('[MapLocationPicker] Failed to open location picker:', error);
      // Show user-friendly error
      showAlert('Failed to load map. Please check your internet connection and try again.', 'Map Error', '🗺️');
      // Clean up any partial state
      this.destroy();
      // Re-throw so caller knows it failed
      throw error;
    }
  }

  /**
   * Close the modal and clean up
   */
  close(): void {
    if (this.modalElement) {
      this.modalElement.classList.remove('visible');
      setTimeout(() => {
        this.destroy();
      }, 300);
    }
  }

  private async loadLeaflet(): Promise<void> {
    if (typeof L !== 'undefined') {
      return;
    }

    return new Promise((resolve, reject) => {
      // Load CSS
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      css.crossOrigin = '';
      document.head.appendChild(css);

      // Load JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Leaflet'));
      document.head.appendChild(script);
    });
  }

  private injectStyles(): void {
    // Only inject once
    if (document.getElementById('map-location-picker-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'map-location-picker-styles';
    style.textContent = `
      .map-location-picker-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      .map-location-picker-modal.visible {
        opacity: 1;
        visibility: visible;
      }
      .mlp-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }
      .mlp-content {
        position: relative;
        width: 90vw;
        max-width: 800px;
        max-height: 85vh;
        background: rgba(20, 20, 30, 0.95);
        backdrop-filter: blur(30px);
        -webkit-backdrop-filter: blur(30px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 24px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        transform: scale(0.95) translateY(10px);
        transition: transform 0.3s ease;
      }
      .map-location-picker-modal.visible .mlp-content {
        transform: scale(1) translateY(0);
      }
      .mlp-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        background: transparent;
      }
      .mlp-header h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
      }
      .mlp-close-btn {
        background: none;
        border: none;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 4px 8px;
        color: rgba(255, 255, 255, 0.7);
        border-radius: 6px;
        transition: background 0.2s ease, color 0.2s ease;
      }
      .mlp-close-btn:hover {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.95);
      }
      .mlp-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        min-height: 0;
      }
      .mlp-search-container {
        position: relative;
        padding: 12px 16px;
        background: rgba(0, 0, 0, 0.2);
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      }
      .mlp-search-icon {
        position: absolute;
        left: 28px;
        top: 50%;
        transform: translateY(-50%);
        color: rgba(255, 255, 255, 0.5);
        pointer-events: none;
      }
      .mlp-search-input {
        width: 100%;
        padding: 12px 16px 12px 40px;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 10px;
        font-size: 15px;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
        box-sizing: border-box;
      }
      .mlp-search-input:focus {
        outline: none;
        border-color: #007AFF;
        box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.3);
        background: rgba(255, 255, 255, 0.15);
      }
      .mlp-search-input::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }
      .mlp-suggestions {
        position: absolute;
        top: calc(100% - 4px);
        left: 16px;
        right: 16px;
        background: rgba(20, 20, 30, 0.98);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 10px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
        max-height: 280px;
        overflow-y: auto;
        z-index: 1000;
        display: none;
      }
      .mlp-suggestions.visible {
        display: block;
      }
      .mlp-suggestion-item {
        padding: 12px 16px;
        cursor: pointer;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        transition: background 0.15s ease;
      }
      .mlp-suggestion-item:last-child {
        border-bottom: none;
      }
      .mlp-suggestion-item:hover {
        background: rgba(74, 144, 226, 0.3);
      }
      .mlp-suggestion-name {
        font-weight: 500;
        font-size: 14px;
        color: rgba(255, 255, 255, 0.95);
        margin-bottom: 2px;
      }
      .mlp-suggestion-address {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .mlp-no-results {
        padding: 20px 16px;
        text-align: center;
        color: rgba(255, 255, 255, 0.5);
        font-size: 14px;
      }
      .mlp-map-container {
        flex: 1;
        min-height: 350px;
        background: #1a1a2e;
      }
      .mlp-marker-icon {
        background: none !important;
        border: none !important;
      }
      .mlp-marker-pin {
        width: 32px;
        height: 32px;
        background: #FF6B6B;
        border: 3px solid white;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 3px 12px rgba(0, 0, 0, 0.35);
        position: relative;
      }
      .mlp-marker-pin::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 10px;
        height: 10px;
        background: white;
        border-radius: 50%;
        transform: translate(-50%, -50%);
      }
      .mlp-footer {
        padding: 16px 20px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
      }
      .mlp-info {
        flex: 1;
        min-width: 200px;
      }
      .mlp-place-name {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.95);
        font-weight: 500;
        margin-bottom: 8px;
        transition: color 0.2s ease;
      }
      .mlp-place-name.loading {
        color: rgba(255, 255, 255, 0.5);
        font-style: italic;
      }
      .mlp-place-name.error {
        color: #FF6B6B;
      }
      .mlp-coordinates {
        display: flex;
        gap: 16px;
        font-size: 13px;
      }
      .mlp-coordinates label {
        display: flex;
        align-items: center;
        gap: 6px;
        color: rgba(255, 255, 255, 0.7);
      }
      .mlp-coordinates span {
        font-weight: 500;
      }
      .mlp-coordinates input {
        width: 110px;
        padding: 6px 8px;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 6px;
        font-family: 'SF Mono', Monaco, 'Courier New', monospace;
        font-size: 12px;
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
      .mlp-coordinates input:focus {
        outline: none;
        border-color: #007AFF;
        background: rgba(255, 255, 255, 0.15);
      }
      .mlp-actions {
        display: flex;
        gap: 12px;
      }
      .mlp-btn {
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
      }
      .mlp-btn-secondary {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.15);
      }
      .mlp-btn-secondary:hover {
        background: rgba(255, 255, 255, 0.15);
      }
      .mlp-btn-primary {
        background: #007AFF;
        color: white;
      }
      .mlp-btn-primary:hover:not(:disabled) {
        background: #0066DD;
      }
      .mlp-btn-primary:disabled {
        background: rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.4);
        cursor: not-allowed;
      }
      @media (max-width: 600px) {
        .mlp-content {
          width: 100%;
          height: 100%;
          max-width: none;
          max-height: none;
          border-radius: 0;
        }
        .mlp-map-container {
          min-height: 300px;
        }
        .mlp-coordinates {
          flex-direction: column;
          gap: 8px;
        }
        .mlp-coordinates input {
          width: 140px;
        }
        .mlp-footer {
          flex-direction: column;
          align-items: stretch;
        }
        .mlp-actions {
          width: 100%;
          justify-content: stretch;
        }
        .mlp-btn {
          flex: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }

  private createModal(): void {
    const stageName = this.escapeHtml(this.options.stageName || 'Stage');
    const hasInitialPosition =
      this.options.initialPosition &&
      (this.options.initialPosition.lat !== 0 || this.options.initialPosition.lng !== 0);

    this.modalElement = document.createElement('div');
    this.modalElement.className = 'map-location-picker-modal';
    this.modalElement.innerHTML = `
      <div class="mlp-backdrop"></div>
      <div class="mlp-content">
        <div class="mlp-header">
          <h3>Select Location for "${stageName}"</h3>
          <button class="mlp-close-btn" aria-label="Close">&times;</button>
        </div>
        <div class="mlp-body">
          <div class="mlp-search-container">
            <svg class="mlp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input type="text" class="mlp-search-input" placeholder="Search for a place, address, or landmark...">
            <div class="mlp-suggestions"></div>
          </div>
          <div class="mlp-map-container"></div>
        </div>
        <div class="mlp-footer">
          <div class="mlp-info">
            <div class="mlp-place-name">${hasInitialPosition ? 'Loading location...' : 'Click on the map or search for a location'}</div>
            <div class="mlp-coordinates">
              <label>
                <span>Lat:</span>
                <input type="number" class="mlp-coord-lat" step="0.000001" placeholder="0.000000" value="${hasInitialPosition ? this.options.initialPosition!.lat.toFixed(6) : ''}">
              </label>
              <label>
                <span>Lng:</span>
                <input type="number" class="mlp-coord-lng" step="0.000001" placeholder="0.000000" value="${hasInitialPosition ? this.options.initialPosition!.lng.toFixed(6) : ''}">
              </label>
            </div>
          </div>
          <div class="mlp-actions">
            <button class="mlp-btn mlp-btn-secondary mlp-cancel-btn">Cancel</button>
            <button class="mlp-btn mlp-btn-primary mlp-confirm-btn" ${hasInitialPosition ? '' : 'disabled'}>Confirm Location</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.modalElement);

    // Get DOM references
    this.searchInput = this.modalElement.querySelector('.mlp-search-input');
    this.suggestionsContainer = this.modalElement.querySelector('.mlp-suggestions');
    this.placeNameDisplay = this.modalElement.querySelector('.mlp-place-name');
    this.latInput = this.modalElement.querySelector('.mlp-coord-lat');
    this.lngInput = this.modalElement.querySelector('.mlp-coord-lng');
    this.confirmBtn = this.modalElement.querySelector('.mlp-confirm-btn');
  }

  private initializeMap(): void {
    const mapContainer = this.modalElement?.querySelector(
      '.mlp-map-container'
    ) as HTMLElement;
    if (!mapContainer) {
      throw new Error('Map container not found in modal');
    }

    // Check if Leaflet loaded successfully
    if (typeof L === 'undefined') {
      throw new Error('Leaflet library failed to load. Check internet connection.');
    }

    try {
      const style = this.options.mapStyle || 'streets';
      const tileConfig = getTileLayerConfig(style);

      const hasInitialPosition =
        this.options.initialPosition &&
        (this.options.initialPosition.lat !== 0 || this.options.initialPosition.lng !== 0);

      const center = hasInitialPosition
        ? [this.options.initialPosition!.lat, this.options.initialPosition!.lng]
        : [DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng];

      const zoom = hasInitialPosition ? this.options.initialZoom || 15 : 4;

      this.map = L.map(mapContainer, {
        center: center as [number, number],
        zoom,
      });

      L.tileLayer(tileConfig.url, {
        attribution: tileConfig.attribution,
        maxZoom: 19,
      }).addTo(this.map);

      // Add initial marker if position provided
      if (hasInitialPosition) {
        this.updateMarker(
          this.options.initialPosition!.lat,
          this.options.initialPosition!.lng,
          false
        );
        this.updatePlaceName(
          this.options.initialPosition!.lat,
          this.options.initialPosition!.lng
        );
      }

      // Handle map clicks
      this.map.on('click', (e: L.LeafletMouseEvent) => {
        this.handleMapClick(e.latlng.lat, e.latlng.lng);
      });

      // Fix map size after modal animation
      setTimeout(() => {
        this.map?.invalidateSize();
      }, 350);
    } catch (error) {
      console.error('[MapLocationPicker] Failed to initialize map:', error);
      throw new Error(`Map initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private setupEventListeners(): void {
    if (!this.modalElement) return;

    // Search input
    this.searchInput?.addEventListener('input', () => this.handleSearchInput());
    this.searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideSuggestions();
      }
    });

    // Click outside suggestions to close
    document.addEventListener('click', this.handleDocumentClick);

    // Coordinate inputs
    const handleCoordChange = () => {
      const lat = parseFloat(this.latInput?.value || '');
      const lng = parseFloat(this.lngInput?.value || '');
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        this.updateMarker(lat, lng, true);
        this.updatePlaceName(lat, lng);
        this.enableConfirm();
      }
    };
    this.latInput?.addEventListener('change', handleCoordChange);
    this.lngInput?.addEventListener('change', handleCoordChange);

    // Close button
    this.modalElement.querySelector('.mlp-close-btn')?.addEventListener('click', () => {
      this.options.onCancel();
      this.close();
    });

    // Backdrop click
    this.modalElement.querySelector('.mlp-backdrop')?.addEventListener('click', () => {
      this.options.onCancel();
      this.close();
    });

    // Cancel button
    this.modalElement.querySelector('.mlp-cancel-btn')?.addEventListener('click', () => {
      this.options.onCancel();
      this.close();
    });

    // Confirm button
    this.confirmBtn?.addEventListener('click', () => {
      this.handleConfirm();
    });

    // Escape key
    document.addEventListener('keydown', this.handleKeyDown);
  }

  private handleDocumentClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (!target.closest('.mlp-search-container')) {
      this.hideSuggestions();
    }
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.options.onCancel();
      this.close();
    }
  };

  private handleMapClick(lat: number, lng: number): void {
    this.updateMarker(lat, lng, false);
    this.updatePlaceName(lat, lng);
    this.enableConfirm();
  }

  private updateMarker(lat: number, lng: number, flyTo: boolean): void {
    if (!this.map) return;

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else {
      // Create custom icon
      const icon = L.divIcon({
        className: 'mlp-marker-icon',
        html: `<div class="mlp-marker-pin"></div>`,
        iconSize: [32, 42],
        iconAnchor: [16, 42],
      });

      this.marker = L.marker([lat, lng], {
        icon,
        draggable: true,
      }).addTo(this.map);

      // Handle drag
      this.marker.on('dragend', () => {
        const pos = this.marker?.getLatLng();
        if (pos) {
          this.updateCoordinateInputs(pos.lat, pos.lng);
          this.updatePlaceName(pos.lat, pos.lng);
        }
      });
    }

    // Update coordinate inputs
    this.updateCoordinateInputs(lat, lng);

    if (flyTo) {
      this.map.flyTo([lat, lng], Math.max(this.map.getZoom(), 15), {
        duration: 0.5,
      });
    }
  }

  private updateCoordinateInputs(lat: number, lng: number): void {
    if (this.latInput) this.latInput.value = lat.toFixed(6);
    if (this.lngInput) this.lngInput.value = lng.toFixed(6);
  }

  private async updatePlaceName(lat: number, lng: number): Promise<void> {
    if (this.placeNameDisplay) {
      this.placeNameDisplay.textContent = 'Looking up location...';
      this.placeNameDisplay.classList.add('loading');
    }

    const result = await this.geocodingService.reverseGeocode(lat, lng);

    if (this.placeNameDisplay) {
      this.placeNameDisplay.classList.remove('loading');
    }

    if (result) {
      this.currentPlaceName = result.shortName || result.displayName.split(',')[0];
      if (this.placeNameDisplay) {
        // Show short name + city/country for context
        let displayText = result.shortName;
        if (result.address?.city) {
          displayText += `, ${result.address.city}`;
        }
        if (result.address?.country) {
          displayText += `, ${result.address.country}`;
        }
        this.placeNameDisplay.textContent = displayText;
      }
    } else {
      this.currentPlaceName = null;
      if (this.placeNameDisplay) {
        this.placeNameDisplay.textContent = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    }
  }

  private handleSearchInput(): void {
    if (this.searchDebounceTimer) {
      window.clearTimeout(this.searchDebounceTimer);
    }

    const query = this.searchInput?.value || '';

    if (query.length < 2) {
      this.hideSuggestions();
      return;
    }

    this.searchDebounceTimer = window.setTimeout(async () => {
      const results = await this.geocodingService.search(query);
      this.showSuggestions(results);
    }, 300);
  }

  private showSuggestions(suggestions: SearchSuggestion[]): void {
    if (!this.suggestionsContainer) return;

    if (suggestions.length === 0) {
      this.suggestionsContainer.innerHTML =
        '<div class="mlp-no-results">No results found</div>';
    } else {
      this.suggestionsContainer.innerHTML = suggestions
        .map(
          (s) => `
        <div class="mlp-suggestion-item" data-lat="${s.lat}" data-lng="${s.lng}" data-name="${this.escapeHtml(s.shortName)}">
          <div class="mlp-suggestion-name">${this.escapeHtml(s.shortName)}</div>
          <div class="mlp-suggestion-address">${this.escapeHtml(s.displayName)}</div>
        </div>
      `
        )
        .join('');

      // Add click handlers
      this.suggestionsContainer.querySelectorAll('.mlp-suggestion-item').forEach((item) => {
        item.addEventListener('click', () => {
          const lat = parseFloat(item.getAttribute('data-lat') || '0');
          const lng = parseFloat(item.getAttribute('data-lng') || '0');
          const name = item.getAttribute('data-name') || '';
          this.selectSuggestion(lat, lng, name);
        });
      });
    }

    this.suggestionsContainer.classList.add('visible');
  }

  private hideSuggestions(): void {
    this.suggestionsContainer?.classList.remove('visible');
  }

  private selectSuggestion(lat: number, lng: number, name: string): void {
    this.hideSuggestions();
    if (this.searchInput) {
      this.searchInput.value = name;
    }

    this.currentPlaceName = name;
    this.updateMarker(lat, lng, true);

    if (this.placeNameDisplay) {
      this.placeNameDisplay.textContent = name;
    }

    this.enableConfirm();
  }

  private enableConfirm(): void {
    if (this.confirmBtn) {
      this.confirmBtn.disabled = false;
    }
  }

  private handleConfirm(): void {
    const lat = parseFloat(this.latInput?.value || '0');
    const lng = parseFloat(this.lngInput?.value || '0');

    if (lat === 0 && lng === 0) {
      // Show error
      if (this.placeNameDisplay) {
        this.placeNameDisplay.textContent = 'Please select a location first';
        this.placeNameDisplay.classList.add('error');
        setTimeout(() => {
          this.placeNameDisplay?.classList.remove('error');
        }, 2000);
      }
      return;
    }

    this.options.onConfirm({ lat, lng }, this.currentPlaceName || undefined);
    this.close();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private destroy(): void {
    // Clean up event listeners
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeyDown);

    if (this.searchDebounceTimer) {
      window.clearTimeout(this.searchDebounceTimer);
    }

    // Destroy map
    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // Remove modal from DOM
    this.modalElement?.remove();
    this.modalElement = null;
  }
}

/**
 * Helper function to open the location picker
 */
export function openLocationPicker(options: MapLocationPickerOptions): MapLocationPicker {
  const picker = new MapLocationPicker(options);
  picker.open();
  return picker;
}
